import { Component, OnInit, OnDestroy, Input, AfterViewInit, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { AuftragEx, CustomAntwort, ChecklisteEintragConfig, BildVO } from 'src/app/api/model/model';

import { Checkliste, ChecklisteEintrag, ChecklisteStatus } from 'src/app/api/model/swagger-model';
import { FormatHelper } from 'src/app/api/helper/format-helper';
import { AuftragService } from 'src/app/api/auftrag.service';
import { Auftragsstatus } from 'src/app/api/model/aufteagsstatus';
import { ChecklistHelper } from 'src/app/api/helper/checklist-helper';
import { AppStorage } from 'src/app/api/helper/app-storage';
import { FotosPage } from '../fotos/fotos.component';
import { Auftragdetails } from 'src/app/auftragsliste/auftragdetails/auftragdetails.helper';
import { Utils } from 'src/app/api/helper/utils';

@Component({
    selector: 'app-auftrag-checkliste',
    templateUrl: './auftrag-checkliste.component.html',
    styleUrls: ['./auftrag-checkliste.component.scss'],
    standalone: false
})
export class AppAuftragChecklisteComponent implements OnInit, OnDestroy, AfterViewInit {
    log = new Logger('AppAuftragChecklisteComponent');

    @ViewChildren(FotosPage) fotos: FotosPage[];

    @Input() auftrag: AuftragEx = null;
    @Input() auftragdetails: Auftragdetails;
    // @Input() workflow: Workflow = null;
    @Input() checkliste: Checkliste = null;
    @Input() titelAnzeigen = true;

    subscriptions: Subscription[] = [];
    eintraege: ChecklisteEintragVo[] = [];

    istAuftragAbgeschlossen = false;
    istReadonly = false;
    isVisible = false;
    fotosAktualisiert = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private auftragService: AuftragService) { }

    async ngOnInit() {
        // if (this.workflow && this.workflow.checkliste) {
        //     this.checkliste = await this.stammdatenService.getCheckliste(this.workflow.checkliste);

        // }

        this.eintraege = [];

        const list = this.checkliste.eintraege.sort((a, b) => a.sort - b.sort);

        for (const p of list) {
            if (p.Aktiv === false) {
                continue;
            }

            const config = ChecklistHelper.parseConfig(p);

            const vo: ChecklisteEintragVo = {
                eintrag: p,
                antwort: '',
                freitext: '',
                moeglicheAntworten: [],
                config,
                jaButtonColor: new BehaviorSubject<string>('light'),
                neinButtonColor: new BehaviorSubject<string>('light'),
                bilder: []
            };

            if (p.antworten == '4') {
                vo.moeglicheAntworten = config.moeglicheAntworten;
            }

            this.eintraege.push(vo);
        }

        if (!this.auftrag.ChecklistenErgebnis) {
            this.auftrag.ChecklistenErgebnis = [];
        }

        let checklistErgebnis = this.auftrag.ChecklistenErgebnis.find(p => p.ChecklisteName == this.checkliste.name);

        // Initialisiere schon einmal ein Ergebnis für die Checkliste. Ansonsten gibt es Probleme später beim Prüfen auf Vollständigkeit.
        // Das ist aber eigentlich nur notwendig wenn Default-Werte vorhanden sind.
        // siehe: pruefeChecklistenVollstaendig
        if (!checklistErgebnis) {
            checklistErgebnis = {
                ChecklisteName: this.checkliste.name,
                Eintraege: []
            };

            this.auftrag.ChecklistenErgebnis.push(checklistErgebnis);
        }

        if (checklistErgebnis) {
            if (!checklistErgebnis.Eintraege) {
                checklistErgebnis.Eintraege = [];
            }

            for (const eintrag of checklistErgebnis.Eintraege) {
                const vo = this.eintraege.find(p => p.eintrag.key === eintrag.Key);

                if (!vo) {
                    continue;
                }

                if (eintrag.Status) {
                    vo.antwort = eintrag.Status;
                }

                if (vo.config?.freitext && eintrag.IsFreitextAusgewaehlt) {
                    vo.antwort = vo.config.freitext;
                    vo.freitext = eintrag.Status;
                }

                if (eintrag.Bilder?.length) {
                    vo.bilder = [];

                    for (const bild of eintrag.Bilder) {
                        vo.bilder.push({
                            datum: bild.Datum,
                            guid: bild.BildGuid,
                            src: null,
                            bild: await AppStorage.current.getBildSrc(bild.BildGuid)
                        });
                    }
                }
            }
        }

        this.updateReadOnly();

        if (!this.istReadonly) {
            // Default-Werte setzen wenn noch keine Auswahl getroffen wurde
            if (!checklistErgebnis || !checklistErgebnis.Eintraege || checklistErgebnis.Eintraege.length == 0) {
                for (const vo of this.eintraege) {
                    if (vo.eintrag.antworten == '5') {
                        // Toggle
                        if (!vo.config.default) {
                            vo.config.default = 'nein';
                        }
                    }

                    if (vo.config.default && !vo.antwort) {
                        vo.antwort = vo.config.default;

                        // Wert direkt speichern. Ansonsten funktioniert die Prüfung auf Vollständigkeit nicht
                        // this.afterAntwortChanged(vo);
                    }
                }
            }
        }

        for (const vo of this.eintraege) {
            this.aktualisiereButtonColor(vo);
        }

        if (this.auftragdetails) {
            this.subscriptions.push(this.auftragdetails.isLieferungAendernGesperrtChanged.subscribe(p => this.updateReadOnly()));
        }

        await this.updateFotos();
    }

    updateReadOnly() {
        this.istAuftragAbgeschlossen = this.auftrag.Auftragsstatus >= Auftragsstatus.Abgeschlossen;

        this.istReadonly = this.auftragdetails?.isLieferungAendernGesperrt ?? false;

        if (this.istAuftragAbgeschlossen) {
            this.istReadonly = true;
        }

        this.cdr.detectChanges();
    }

    async updateFotos() {
        this.cdr.detectChanges();

        if (this.fotos?.length) {
            await Utils.delay(100);

            this.cdr.detectChanges();

            for (const fotoPage of this.fotos) {
                await fotoPage.updateBilder('auftrag-checkliste');
            }

            this.fotosAktualisiert = true;
        }
    }

    async ngAfterViewInit() {
        this.log?.info('ngAfterViewInit');

        if (!this.fotosAktualisiert) {
            await this.updateFotos();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    async ionViewWillEnter() {
    }

    ionViewDidEnter() {
    }

    ionViewWillLeave() {
    }

    antwortChanged(e: Event, vo: ChecklisteEintragVo) {
        this.log?.debug('antwortChanged', { e, vo });

        const event = e as CustomEvent;

        vo.antwort = event.detail.value;

        this.afterAntwortChanged(vo);
    }

    toggleAntwortChanged(e: Event, vo: ChecklisteEintragVo) {
        this.log?.info(`toggleAntwortChanged ${vo.eintrag.text}. Alt=${vo.antwort}`);

        const event = e as CustomEvent;

        vo.antwort = event.detail.checked ? 'ja' : 'nein';

        this.afterAntwortChanged(vo);
    }

    afterAntwortChanged(vo: ChecklisteEintragVo) {
        let checklisteStatus = this.getChecklistStatus(vo);

        if (vo.config?.freitext && vo.config.freitext === vo.antwort) {
            checklisteStatus.IsFreitextAusgewaehlt = true;
            checklisteStatus.Status = vo.freitext;
        } else {
            checklisteStatus.IsFreitextAusgewaehlt = false;
            checklisteStatus.Status = vo.antwort;
        }

        this.auftragService.speichereAuftrag(this.auftrag);
    }

    aktualisiereButtonColor(vo: ChecklisteEintragVo) {
        // if (vo.antwort == 'ja') {
        //     vo.jaButtonColor.next('success');
        //     vo.neinButtonColor.next('light');
        // } else if (vo.antwort == 'nein') {
        //     vo.jaButtonColor.next('light');
        //     vo.neinButtonColor.next('danger');
        // } else {
        //     vo.jaButtonColor.next('light');
        //     vo.neinButtonColor.next('light');
        // }

        if (vo.antwort == 'ja') {
            vo.jaButtonColor.next('success');
            vo.neinButtonColor.next('light');
        } else if (vo.antwort == 'nein') {
            vo.jaButtonColor.next('light');
            vo.neinButtonColor.next('secondary');
        } else {
            vo.jaButtonColor.next('light');
            vo.neinButtonColor.next('light');
        }
    }

    isChecked(vo: ChecklisteEintragVo, a: CustomAntwort) {
        if (!this.auftrag || !this.checkliste) {
            return false;
        }

        if (!this.auftrag.ChecklistenErgebnis) {
            return false;
        }

        const ergebnis = this.auftrag.ChecklistenErgebnis.find(p => p.ChecklisteName == this.checkliste.name);

        if (!ergebnis || !ergebnis.Eintraege) {
            return false;
        }

        const ergebnisEintrag = ergebnis.Eintraege.find(p => p.Key == vo.eintrag.key);

        if (!ergebnisEintrag) {
            return false;
        }

        return ergebnisEintrag.Status == a.value;
    }

    onAntwortClicked(vo: ChecklisteEintragVo, value: any) {
        this.log?.info('onAntwortClicked', {
            vo,
            value
        });

        if (this.istReadonly) {
            return;
        }

        if (this.auftrag.Auftragsstatus < Auftragsstatus.Abgeschlossen) {
            vo.antwort = value;
            this.afterAntwortChanged(vo);
        }

        this.aktualisiereButtonColor(vo);
    }

    onNeuesBild(vo: ChecklisteEintragVo, bild: BildVO) {
        this.log?.debug('onNeuesBild', { vo, bild });

        let checklisteStatus = this.getChecklistStatus(vo);

        checklisteStatus.Bilder.push({
            Datum: bild.datum,
            BildGuid: bild.guid,
            // Bild: bild.bild,
            Typ: 'Checkliste',
            Bezeichnung: vo.eintrag.text
        });

        this.auftragService.speichereAuftrag(this.auftrag);
    }

    onBildGeloescht(vo: ChecklisteEintragVo, guid: string) {
        let checklisteStatus = this.getChecklistStatus(vo);

        checklisteStatus.Bilder = checklisteStatus.Bilder.filter(p => p.BildGuid != guid);

        this.auftragService.speichereAuftrag(this.auftrag);
    }

    getChecklistStatus(vo: ChecklisteEintragVo): ChecklisteStatus {
        if (!this.auftrag.ChecklistenErgebnis) {
            this.auftrag.ChecklistenErgebnis = [];
        }

        let checklistErgebnis = this.auftrag.ChecklistenErgebnis.find(p => p.ChecklisteName == this.checkliste.name);

        if (!checklistErgebnis) {
            checklistErgebnis = {
                ChecklisteName: this.checkliste.name,
                Eintraege: []
            };

            this.auftrag.ChecklistenErgebnis.push(checklistErgebnis);
        }

        let checklisteStatus: ChecklisteStatus = checklistErgebnis.Eintraege.find(p => p.Key === vo.eintrag.key);

        if (!checklisteStatus) {
            checklisteStatus = {
                Key: vo.eintrag.key,
                Text: vo.eintrag.text,
                Status: '',
                Bilder: []
            };

            checklistErgebnis.Eintraege.push(checklisteStatus);
        }

        if (!checklisteStatus.Bilder) {
            checklisteStatus.Bilder = [];
        }

        return checklisteStatus;
    }
}

interface ChecklisteEintragVo {
    eintrag: ChecklisteEintrag;
    antwort: string;
    freitext: string;
    moeglicheAntworten: CustomAntwort[];
    config: ChecklisteEintragConfig;
    bilder: BildVO[];

    jaButtonColor: BehaviorSubject<string>;
    neinButtonColor: BehaviorSubject<string>;
}